import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';

const PROVIDER_LABELS = {
  seedance: 'Seedance 2',
  runway: 'Runway',
  minimax: 'MiniMax',
  veo3: 'Google Veo',
  ltx: 'LTX',
  nano_banana: 'Nano Banana',
  narration_slideshow: 'Slideshow',
};

export function VideoGenerationPromptDialog({
  open,
  onOpenChange,
  provider,
  initialPrompt = '',
  maxLength = 2000,
  loading = false,
  title = 'Review video prompt',
  creditEstimate = null,
  onConfirm,
}) {
  const [draft, setDraft] = useState(initialPrompt);

  useEffect(() => {
    if (open) setDraft(initialPrompt || '');
  }, [open, initialPrompt]);

  const provLabel = PROVIDER_LABELS[provider] || provider || 'Video';
  const len = (draft || '').length;
  const over = maxLength > 0 && len > maxLength;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="video-generation-prompt-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Spoken lines are in script order, followed by your scene direction and camera notes.
            Edit the prompt below, then confirm to start {provLabel} generation.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 min-h-0 flex flex-col gap-2">
          <Label htmlFor="video-gen-prompt-textarea">Video prompt sent to API</Label>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Building prompt…
            </div>
          ) : (
            <Textarea
              id="video-gen-prompt-textarea"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="min-h-[220px] max-h-[50vh] font-mono text-sm resize-y"
              data-testid="video-generation-prompt-textarea"
            />
          )}
          <p className={`text-xs ${over ? 'text-destructive' : 'text-muted-foreground'}`}>
            {len}
            {maxLength > 0 ? ` / ${maxLength} characters` : ' characters'}
            {over ? ' — shorten before generating' : ''}
          </p>
          {creditEstimate ? (
            <p className="text-xs font-medium text-[#6366F1] bg-[#EEF2FF] rounded-lg px-3 py-2">
              Estimated cost: {creditEstimate}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={loading || !draft.trim() || over}
            onClick={() => onConfirm(draft.trim())}
            data-testid="video-generation-prompt-confirm"
            style={{ background: '#f97316', color: 'white' }}
          >
            Generate video
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
